import { Box, Typography } from "@mui/material";
import logo from "../pages/images/logo.png";

/**
 * Format a DD-MM-YYYY date string to ordinal format like "3rd of March 2026"
 */
const formatDateOrdinal = (dateStr) => {
  if (!dateStr) return "-";
  const [dd, mm, yyyy] = dateStr.split("-");
  const day = parseInt(dd);
  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const month = monthNames[parseInt(mm) - 1];

  const suffix = (d) => {
    if (d > 3 && d < 21) return "th";
    switch (d % 10) {
      case 1:
        return "st";
      case 2:
        return "nd";
      case 3:
        return "rd";
      default:
        return "th";
    }
  };

  return `${day}${suffix(day)} of ${month} ${yyyy}`;
};

/**
 * Renders the curriculum type with strikethrough for non-applicable types.
 * e.g., if curriculumType is "MIXED", renders: (~~Local~~ / Mixed / ~~London~~)
 */
const CurriculumTypeDisplay = ({ type }) => {
  const types = [
    { key: "LOCAL", label: "Local" },
    { key: "MIXED", label: "Mixed" },
    { key: "LONDON", label: "London" },
  ];

  return (
    <span>
      (
      {types.map((t, i) => (
        <span key={t.key}>
          {i > 0 && " / "}
          <span
            style={
              t.key !== type
                ? { textDecoration: "line-through" }
                : { fontWeight: "bold" }
            }
          >
            {t.label}
          </span>
        </span>
      ))}
      )
    </span>
  );
};

const LeavingCertificate = ({ student, certificate }) => {
  const parentName = student.parent?.fullName || "-";
  const className = student.class?.name || "-";
  const admissionNumber = `MRI/${student.yearOfEntry}/${student.id}`;

  return (
    <Box sx={{ fontFamily: "serif", color: "#000", p: 0 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <img
          src={logo}
          alt="MRI Logo"
          style={{ width: 100, marginTop: "-30px" }}
        />
        <Box sx={{ textAlign: "center" }}>
          <Typography sx={{ fontWeight: 700, fontFamily: "serif" }}>
            Mount Royal International School
          </Typography>
          <Typography
            sx={{
              fontWeight: 700,
              fontFamily: "serif",
              textDecoration: "underline",
            }}
          >
            Student Leaving Certificate
          </Typography>
        </Box>
        <Box sx={{ textAlign: "right" }}>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.8rem" }}>
            67, Kawdana Road
          </Typography>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.8rem" }}>
            Dehiwala, Sri Lanka
          </Typography>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.8rem" }}>
            0112712149
          </Typography>
        </Box>
      </Box>

      {/* Personal Details */}
      <Typography
        sx={{ fontWeight: 700, mb: 0.5, fontFamily: "serif", fontSize: "1rem" }}
      >
        Personal Details
      </Typography>
      <Box sx={{ mb: 1.5, pl: 1 }}>
        <DetailRow label="Student Name" value={student.fullName} bold />
        <DetailRow
          label="Date of Birth"
          value={formatDateOrdinal(student.dateOfBirth)}
        />
        <DetailRow label="Name of Parent" value={parentName} bold />
      </Box>

      {/* Academic Details */}
      <Typography
        sx={{ fontWeight: 700, mb: 0.5, fontFamily: "serif", fontSize: "1rem" }}
      >
        Academic Details
      </Typography>
      <Box sx={{ mb: 1.5, pl: 1 }}>
        <DetailRow
          label="Date of Admission"
          value={formatDateOrdinal(student.admissionDate)}
        />
        <DetailRow label="Admission Number" value={admissionNumber} />
        <DetailRow
          label="Name of Previous School"
          value={student.previousSchool || "-"}
        />
        <DetailRow
          label="Grade of Admission"
          value={
            student.gradeOfAdmission ? `Grade ${student.gradeOfAdmission}` : "-"
          }
        />
        <DetailRow
          label="Curriculum Type"
          value={<CurriculumTypeDisplay type={student.curriculumType} />}
        />
        <DetailRow label="Current Grade" value={className} />
        <DetailRow label="Character" value={certificate.character} />
        <DetailRow
          label="Date of Leaving"
          value={formatDateOrdinal(certificate.leavingDate)}
        />
        <DetailRow
          label="Reason for Leaving"
          value={
            certificate.reasonForLeaving === "OTHER"
              ? certificate.reasonForLeavingOther
              : formatReasonLabel(certificate.reasonForLeaving)
          }
        />
      </Box>

      {/* Achievements — only shown if at least one field has data */}
      {(certificate.academicAchievements || certificate.coCurricularActivities ||
        certificate.specialPostHeld || certificate.scholarshipsAwards) && (
        <>
          <Typography
            sx={{ fontWeight: 700, mb: 0.5, fontFamily: "serif", fontSize: "1rem" }}
          >
            Achievements
          </Typography>
          <Box sx={{ mb: 1.5, pl: 1 }}>
            {certificate.academicAchievements && (
              <DetailRow
                label="Academic Achievements"
                value={certificate.academicAchievements}
              />
            )}
            {certificate.coCurricularActivities && (
              <DetailRow
                label="Co-curricular Activities"
                value={certificate.coCurricularActivities}
              />
            )}
            {certificate.specialPostHeld && (
              <DetailRow
                label="Special Post Held"
                value={certificate.specialPostHeld}
              />
            )}
            {certificate.scholarshipsAwards && (
              <DetailRow
                label="Scholarships/Award Received"
                value={certificate.scholarshipsAwards}
              />
            )}
          </Box>
        </>
      )}

      {/* Attestation */}
      <Typography
        sx={{ fontWeight: 700, mb: 0.5, fontFamily: "serif", fontSize: "1rem" }}
      >
        Attestation
      </Typography>
      <Typography
        sx={{
          mb: 1,
          fontFamily: "serif",
          textAlign: "justify",
          fontSize: "0.9rem",
        }}
      >
        The above details have been checked and found correct. Mount Royal
        International School however accepts no liability from any third party
        with regard to the contents of this certificate.
      </Typography>

      {/* Signature lines */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr 1fr",
          mt: 2,
          mb: 1,
        }}
      >
        <Box>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.9rem" }}>
            Checked by :
          </Typography>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.9rem", mt: 2.5 }}>
            Signature of Principal :
          </Typography>
        </Box>

        <Box />

        <Box>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.9rem" }}>
            Signature :
          </Typography>
          <Typography sx={{ fontFamily: "serif", fontSize: "0.9rem", mt: 2.5 }}>
            Date :
          </Typography>
        </Box>
      </Box>
      {/* Certificate Number Footer */}
      <Typography
        sx={{
          fontFamily: "serif",
          textAlign: "right",
          mt: 1,
          fontSize: "0.85rem",
        }}
      >
        Certificate No: {certificate.certificateNumber}
      </Typography>
    </Box>
  );
};

/** Helper: single label-value row */
const DetailRow = ({ label, value, bold }) => (
  <Box sx={{ display: "flex", mb: 0.5 }}>
    <Typography sx={{ width: 240, fontFamily: "serif" }}>{label}</Typography>
    <Typography sx={{ fontFamily: "serif", fontWeight: bold ? 700 : 400 }}>
      : {value}
    </Typography>
  </Box>
);

/** Helper: format enum to readable label */
const formatReasonLabel = (reason) => {
  const map = {
    MOVING_ABROAD: "Moving Abroad",
    TRANSFER_TO_ANOTHER_SCHOOL: "Transfer to Another School",
    FINANCIAL: "Financial",
    GRADUATED: "Graduated",
    PERSONAL: "Personal",
    OTHER: "Other",
  };
  return map[reason] || reason;
};

export default LeavingCertificate;
